import React from 'react';
import { Content, Portal, Root, Trigger } from '@radix-ui/react-hover-card';
import type { CSS } from '@components-shared/src/stitches';
import { styled } from '@components-shared/src/stitches';
import { animationStyles, contentStyles } from '../shared';
import { useGetLayoutContainer } from '../useGetLayoutContainer';

export function HoverCard({
  children,
  ...props
}: { children: React.ReactNode } & React.ComponentProps<typeof Root>) {
  const { openDelay = 0 } = props;
  return (
    <StyledRoot openDelay={openDelay} {...props}>
      {children}
    </StyledRoot>
  );
}

export function StyledHoverContent({
  children,
  css,
  ...props
}: {
  children: React.ReactNode;
  css?: CSS;
} & React.ComponentProps<typeof Content>) {
  const { sideOffset = 5, side = 'bottom', avoidCollisions = true } = props;

  const layoutElement = useGetLayoutContainer();

  return (
    <Portal>
      <StyledContent
        sideOffset={sideOffset}
        side={side}
        avoidCollisions={avoidCollisions}
        collisionBoundary={layoutElement}
        {...props}
        css={css}
      >
        {children}
      </StyledContent>
    </Portal>
  );
}

function HoverCardContent({
  children,
  /** apply default animation that slides in based on given side prop */
  animate,
  ...props
}: {
  children: React.ReactNode;
  animate?: boolean;
} & React.ComponentProps<typeof Content>) {
  return (
    <ContentWrapper animate={animate} {...props}>
      {children}
    </ContentWrapper>
  );
}

const StyledRoot = styled(Root, {
  backgroundColor: '$neutral900',
});

const StyledContent = styled(Content, {
  ...contentStyles,
  opacity: 1,
  zIndex: '$tooltip',
});

const ContentWrapper = styled(Content, {
  variants: {
    animate: {
      true: {
        ...animationStyles,
      },
    },
  },
});
HoverCard.StyledContent = StyledHoverContent;
HoverCard.Content = HoverCardContent;
HoverCard.Trigger = Trigger;
HoverCard.Portal = Portal;
